/**
 * PresetGallery — browse, search, preview and manage brand presets.

 * Two panels, not a master/detail rail: a grid of preset swatches, and below
 * it the selected preset rendered across every real app surface at full
 * width. The rail it replaced squeezed a scaled full page into a quarter of
 * the column and nested three scrollbars to do it.
 *
 * Two preset sources merged into one list:
 *   • Hardcoded presets from `frontend/src/brand/presets/*.ts` (m2, brij).
 *     These always exist and can be applied but not edited or deleted from
 *     the UI (they live in source).
 *   • DB-backed presets from `/super-admin/brand-presets` — created via
 *     "Save current brand as preset". Editable + deletable.
 *
 * Designer flow:
 *   1. Search — filters by name or description across both sources.
 *   2. Hover/click a card → see a live thumbnail (login surface) before
 *      committing.
 *   3. Click "Apply to current tenant" → loads the preset into the editor.
 *   4. Click "Preview platform" → swaps the global active brand for a
 *      what-if look (does NOT save anything to any tenant).
 *   5. Click "Delete" on a user-authored preset → removes it.
 */

import { useEffect, useMemo, useState } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import { Input, Button, Tooltip, Tag, message, Empty, Spin, Popconfirm } from 'antd';
import {
  listPresetIds,
  getPreset as getHardcodedPreset,
  presetAsOverrides,
  defaultBrand,
} from '@/brand';
import { mergeBrandOverrides } from '@/brand/merge-overrides';
import type { BrandConfig, BrandOverrides } from '@/brand/types';
import {
  listPresets,
  deletePreset,
  type BrandPresetRecord,
} from '../services/brandPresetsService';
import { PreviewScope, PreviewTabs, type PreviewSurface } from './PreviewSurfaces';
import './preset-gallery.css';

/* ─── Unified preset shape (covers both sources) ───────────────────── */

interface UnifiedPreset {
  /** Stable key — uuid for DB presets, hardcoded id for source presets. */
  key: string;
  name: string;
  description?: string;
  overrides: BrandOverrides;
  source: 'db' | 'hardcoded';
  /** Only set on DB presets — null means it can be deleted, true means system row. */
  is_system?: boolean;
  created_at?: string;
}

interface Props {
  /**
   * Called when the user picks a preset to apply. The parent decides what
   * "apply" means — when invoked from the editor, it loads the overrides
   * into the form; when invoked from the gallery toolbar, it swaps the
   * global preview brand.
   */
  onApply: (preset: UnifiedPreset) => void;
  /**
   * Called when the user wants to "preview the platform" in this preset
   * without applying it to any tenant. Does NOT mutate persistent state.
   */
  onPreview?: (preset: UnifiedPreset) => void;
}

/**
 * Body-only component — renders the preset browser inline within the
 * parent page. NO modal wrapper. The parent controls visibility by
 * conditionally rendering this component (in-page step pattern, per
 * the user's "no popups" directive).
 */
export default function PresetGallery({
  onApply, onPreview,
}: Props) {
  const { t } = useLanguage();
  const [query, setQuery] = useState('');
  const [dbPresets, setDbPresets] = useState<BrandPresetRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeKey, setActiveKey] = useState<string | null>(null);

  // Reload DB presets when the gallery mounts — keeps the list fresh
  // after a "Save as preset" round-trip elsewhere.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listPresets()
      .then(rows => { if (!cancelled) setDbPresets(rows); })
      .catch(err => message.error(`Failed to load presets: ${err?.message || err}`))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  /** Combined list, DB first, then hardcoded — DB wins on name collision. */
  const allPresets = useMemo<UnifiedPreset[]>(() => {
    const dbList: UnifiedPreset[] = dbPresets.map(p => ({
      key: `db:${p.preset_id}`,
      name: p.preset_name,
      description: p.preset_description ?? undefined,
      overrides: p.brand_overrides || {},
      source: 'db' as const,
      is_system: p.is_system,
      created_at: p.created_at,
    }));
    const dbNames = new Set(dbList.map(p => p.name.toLowerCase()));
    const hardcoded: UnifiedPreset[] = [];
    for (const id of listPresetIds()) {
      const p = getHardcodedPreset(id);
      if (!p) continue;
      if (dbNames.has(p.name.toLowerCase())) continue;
      hardcoded.push({
        key: `src:${id}`,
        name: p.name,
        description: t('designSystem.builtInPresetShippedWith'),
        overrides: presetAsOverrides(p) || {},
        source: 'hardcoded',
      });
    }
    return [...dbList, ...hardcoded];
  }, [dbPresets]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allPresets;
    return allPresets.filter(p =>
      p.name.toLowerCase().includes(q)
      || (p.description || '').toLowerCase().includes(q),
    );
  }, [allPresets, query]);

  const active = useMemo(
    () => filtered.find(p => p.key === activeKey) ?? filtered[0] ?? null,
    [filtered, activeKey],
  );

  // Reset selection when query changes the visible list.
  useEffect(() => {
    if (filtered.length > 0 && (!activeKey || !filtered.find(p => p.key === activeKey))) {
      setActiveKey(filtered[0].key);
    }
  }, [filtered, activeKey]);

  async function handleDelete(p: UnifiedPreset) {
    if (p.source !== 'db' || p.is_system) return;
    const id = p.key.replace(/^db:/, '');
    try {
      await deletePreset(id);
      setDbPresets(rows => rows.filter(r => r.preset_id !== id));
      message.success(`Deleted preset “${p.name}”`);
    } catch (err: any) {
      message.error(`Delete failed: ${err?.response?.data?.error || err?.message || err}`);
    }
  }

  const dbCount = allPresets.filter(p => p.source === 'db').length;

  return (
    <div className="pg-root" data-testid="preset-gallery">
      {/* ── The catalogue ─────────────────────────────────────────────── */}
      <section className="sa-panel">
        <header className="sa-panel-head pg-head">
          <div className="pg-head-text">
            <h3>{t('superAdmin.presets.title')}</h3>
            <p>{t('superAdmin.presets.subtitle')}</p>
          </div>
          <div className="pg-head-tools">
            <Input
              className="pg-search"
              allowClear
              placeholder={t('superAdmin.presets.search')}
              value={query}
              onChange={e => setQuery(e.target.value)}
              data-testid="preset-search"
            />
            <span className="pg-head-count" data-testid="preset-count">
              {filtered.length === allPresets.length
                ? t('superAdmin.presets.count', { count: allPresets.length })
                : t('superAdmin.presets.countFiltered', { shown: filtered.length, total: allPresets.length })}
              {dbCount > 0 && (
                <span className="pg-head-count-sub">
                  {` · ${t('superAdmin.presets.savedHere', { count: dbCount })}`}
                </span>
              )}
            </span>
          </div>
        </header>

        <div className="pg-grid-wrap">
          {loading && allPresets.length === 0 ? (
            <div className="pg-state"><Spin /></div>
          ) : filtered.length === 0 ? (
            <div className="pg-state">
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={query ? t('superAdmin.presets.noMatch', { query }) : t('superAdmin.presets.empty')}
              />
            </div>
          ) : (
            <ul className="pg-grid" data-testid="preset-gallery-list">
              {filtered.map(p => {
                const merged = mergeBrandOverrides(defaultBrand, p.overrides);
                const isActive = active?.key === p.key;
                return (
                  <li
                    key={p.key}
                    className={`pg-card ${isActive ? 'pg-card--active' : ''}`}
                    data-testid={`preset-card-${p.name}`}
                  >
                    <button
                      type="button"
                      className="pg-card-body"
                      onClick={() => setActiveKey(p.key)}
                      aria-pressed={isActive}
                    >
                      {/* The swatch IS the preset: its gradient, its mark, and
                          its heading face, so the grid can be read at a glance
                          instead of card by card. */}
                      <span
                        className="pg-card-banner"
                        style={{ background: merged.colors.gradientPrimary }}
                      >
                        <img src={merged.icon} alt="" aria-hidden />
                        <span
                          className="pg-card-banner-type"
                          style={{ fontFamily: merged.fonts?.heading || undefined }}
                        >
                          Aa
                        </span>
                      </span>

                      <span className="pg-card-text">
                        <span className="pg-card-name">
                          {p.name}
                          {p.source === 'hardcoded' && <Tag>{t('superAdmin.presets.builtIn')}</Tag>}
                          {p.is_system && <Tag color="blue">{t('superAdmin.presets.system')}</Tag>}
                        </span>
                        <span className="pg-card-desc">
                          {p.description || t('superAdmin.presets.noDescription')}
                        </span>
                        <span className="pg-card-meta">
                          <span
                            className="pg-card-swatch"
                            style={{ background: merged.colors.primary }}
                          />
                          {merged.colors.primary}
                          <span className="pg-card-dot" />
                          {merged.fonts?.heading || t('superAdmin.presets.defaultFont')}
                        </span>
                      </span>
                    </button>

                    {p.source === 'db' && !p.is_system && (
                      <Popconfirm
                        title={t('superAdmin.presets.deleteTitle')}
                        description={t('superAdmin.presets.deleteBody')}
                        okText={t('common.delete')}
                        okButtonProps={{ danger: true }}
                        onConfirm={() => handleDelete(p)}
                      >
                        <button
                          type="button"
                          className="pg-card-delete"
                          aria-label={`Delete preset ${p.name}`}
                          data-testid={`preset-delete-${p.name}`}
                        >
                          ×
                        </button>
                      </Popconfirm>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>

      {/* ── What the selected one looks like ──────────────────────────── */}
      {active && (
        <PresetDetail
          key={active.key}
          preset={active}
          onApply={() => onApply(active)}
          onPreview={onPreview ? () => onPreview(active) : undefined}
        />
      )}
    </div>
  );
}

function PresetDetail({
  preset, onApply, onPreview,
}: {
  preset: UnifiedPreset;
  onApply: () => void;
  onPreview?: () => void;
}) {
  const { t } = useLanguage();
  const merged: BrandConfig = useMemo(
    () => mergeBrandOverrides(defaultBrand, preset.overrides),
    [preset],
  );
  // Same surfaces the editor's own rail offers, so what you judge a preset on
  // is what you will get. Login first: it is the only screen a tenant's people
  // see before they are signed in, so it carries the most brand weight.
  const [previewSurface, setPreviewSurface] = useState<PreviewSurface>('login');

  return (
    <section className="sa-panel pg-detail" data-testid="preset-gallery-detail">
      <header className="sa-panel-head pg-head">
        <div className="pg-head-text">
          <h3 dir="auto">{preset.name}</h3>
          <p>{preset.description || t('superAdmin.presets.noDescription')}</p>
        </div>
        <div className="pg-head-tools pg-detail-actions">
          {onPreview && (
            <Tooltip title={t('superAdmin.presets.previewHint')}>
              <Button onClick={onPreview} data-testid="preset-detail-preview">
                {t('superAdmin.presets.previewAction')}
              </Button>
            </Tooltip>
          )}
          <Tooltip title={t('superAdmin.presets.applyHint')}>
            <Button type="primary" onClick={onApply} data-testid="preset-detail-apply">
              {t('superAdmin.presets.applyAction')}
            </Button>
          </Tooltip>
        </div>
      </header>

      {/* Full width, and the panel's only child — the cramped right rail this
          replaced put a scaled full page into a 1/4-width column inside two
          nested scrollers. */}
      <div className="pg-detail-preview">
        <PreviewScope merged={merged}>
          <PreviewTabs
            merged={merged}
            active={previewSurface}
            onChange={setPreviewSurface}
          />
        </PreviewScope>
      </div>
    </section>
  );
}
